// Lib
import type { MutableRefObject } from 'react';
import { isEqual } from 'lodash';
import type { GridSettings } from 'handsontable/settings';

// Utils
import { adjustTableContentDataSize } from '../utils/dataUtils/grid/tableGridSizingUtils';
import HotTableClipboardSourceManager from '../../bridge/manager/HotTableClipboardSourceManager';
import useRefCallback from '../../../../utils/react/useRefCallback';
import { getColumnCount, getRowCount } from '../../../../../common/table/utils/tableCellDataPropertyUtils';
import { buildHotTableContentData } from '../../bridge/utils/hotInstanceUtils/hotTableDataUtils';
import {
    createCellDataChanges,
    createCellDataChangesFromReplay,
    createCellDataChangesFromSource,
} from '../utils/hotInstanceUtils/cell/hotTableCellEditingUtils';
import { isTableCellMetaChangeSource } from '../utils/tableChangeSourceUtils';
import { setHotTableCellMetaObject } from '../../bridge/utils/hotInstanceUtils/meta/hotTableCellMetaUtils';
import {
    getUndoRedoReplayCellData,
    setPendingUndoRedoActionCellDataChanges,
    type UndoRedoRichCellDataState,
} from '../../bridge/utils/hotInstanceUtils/undoRedo/hotTableUndoRedoRichCellDataUtils';

import { MNHotInstance } from '../../bridge/tableHotInstanceTypes';
import { CellData, CellDataChange, CellValueChange, TableChangeSource } from '../../../../../common/table/TableTypes';

type HotTableHook<Key extends keyof GridSettings> = NonNullable<GridSettings[Key]>;

export type UseTableCellEditingHandlersReturn = {
    beforeChange: HotTableHook<'beforeChange'>;
};

/**
 * Custom hook for handling table cell editing operations
 */
export const useTableCellEditingHandlers = ({
    locale,
    currencyPreference,
    hotTableInstanceRef,
    undoRedoRichCellDataState,
}: {
    locale: string;
    currencyPreference: string;
    hotTableInstanceRef: MutableRefObject<MNHotInstance | null>;
    undoRedoRichCellDataState: UndoRedoRichCellDataState;
}): UseTableCellEditingHandlersReturn => {
    /**
     * Normalises incoming HOT edits into Milanote cell data before HOT commits them.
     *
     * Meta-only change sources are allowed through unchanged so existing persisted cell data stays intact. For user,
     * paste, and autofill edits, this hook rebuilds rich cell payloads, expands the grid if the change reaches beyond
     * the current bounds, rewrites HOT's pending values to the resolved cell values, and mirrors the updated cell data
     * into HOT cell meta so follow-up handlers read the same canonical state.
     */
    const beforeChange = useRefCallback<UseTableCellEditingHandlersReturn['beforeChange']>(
        (changes, source) => {
            const hotInstance = hotTableInstanceRef.current;
            if (!hotInstance || !changes?.length) return;

            // If source is coming from updating cell meta, save data as is
            if (isTableCellMetaChangeSource(source)) return true;

            const cellValueChanges = changes.filter((change): change is CellValueChange => change !== null);

            const data = buildHotTableContentData(hotInstance);

            // Convert cell changes [row, col, prevValue, newValue] to [row, col, prevCellData, newCellData]
            let cellDataChanges: CellDataChange[];

            // Step 4 of undo/redo flow
            // For undo / redo - we need to get the data that was retrieved from the undo action
            //  and apply it to the cell data changes
            if (source === TableChangeSource.UNDO || source === TableChangeSource.REDO) {
                const replayCellDataByCoordinate = new Map<string, CellData>();

                cellValueChanges.forEach(([row, col]) => {
                    const replayCellData = getUndoRedoReplayCellData(undoRedoRichCellDataState, row, Number(col));
                    if (!replayCellData) return;

                    replayCellDataByCoordinate.set(`${row}:${Number(col)}`, replayCellData);
                });

                cellDataChanges = createCellDataChangesFromReplay(
                    cellValueChanges,
                    data,
                    replayCellDataByCoordinate,
                    locale,
                    currencyPreference,
                );
            } else if (source === TableChangeSource.AUTOFILL_FILL || source === TableChangeSource.COPY_PASTE_PASTE) {
                const sourceCellDataArray =
                    source === TableChangeSource.AUTOFILL_FILL
                        ? HotTableClipboardSourceManager.getTableAutofillData()
                        : HotTableClipboardSourceManager.getTableCopiedDataForChanges(cellValueChanges);

                cellDataChanges = createCellDataChangesFromSource(
                    cellValueChanges,
                    data,
                    source,
                    sourceCellDataArray,
                    locale,
                ) as CellDataChange[];
            } else if (source === TableChangeSource.EDIT) {
                // Checkboxes can be toggled without initiating a cell editor,
                // so it's not guaranteed that an MNEditor exists here.
                const content = hotInstance.getMNEditor()?.getContent();
                cellDataChanges = createCellDataChanges(cellValueChanges, data, locale, currencyPreference, content);
            } else {
                cellDataChanges = createCellDataChanges(cellValueChanges, data, locale, currencyPreference, undefined);
            }

            // Adjust table size to fit all the changes
            const nRows = getRowCount(data);
            const nCols = getColumnCount(data);

            const maxChangeRow = Math.max(...cellDataChanges.map(([row]) => row)) + 1;
            const maxChangeCol = Math.max(...cellDataChanges.map(([, col]) => Number(col))) + 1;

            const maxRow = Math.max(maxChangeRow, nRows);
            const maxCol = Math.max(maxChangeCol, nCols);

            const newData = adjustTableContentDataSize(data, maxRow, maxCol);

            const nRowsNew = getRowCount(newData) - nRows;
            const nColsNew = getColumnCount(newData) - nCols;

            if (nRowsNew > 0) {
                hotInstance.alter('insert_row_below', nRows - 1, nRowsNew, source);
            }

            if (nColsNew > 0) {
                hotInstance.alter('insert_col_end', nCols - 1, nColsNew, source);
            }

            // Apply changes if any
            cellDataChanges.forEach((cellDataChange, index) => {
                const [row, col, _previousCellData, newCellData] = cellDataChange;
                const numericCol = Number(col);

                cellValueChanges[index][3] = newCellData.value;
                newData[row][numericCol] = newCellData;

                setHotTableCellMetaObject(hotInstance, row, numericCol, { cellData: newCellData });
            });

            if (isEqual(newData, data)) return;

            // Step 1 of the undo/redo flow
            //  Store the changes associated with the HOT instance so it can be used in a future undo/redo action
            setPendingUndoRedoActionCellDataChanges(undoRedoRichCellDataState, cellDataChanges);
        },
        [currencyPreference, hotTableInstanceRef, locale, undoRedoRichCellDataState],
    );

    return {
        beforeChange,
    };
};

export default useTableCellEditingHandlers;
